/**
 * Declarative realtime subscription helpers.
 *
 * `useCallRoom` / `useHotelRoom` subscribe a component (via effect) to the
 * Socket.IO rooms for the duration it is mounted. The registered cache handlers
 * then patch the TanStack Query cache, so call/transcript queries update live.
 *
 * Wrapping the subscription in a tiny component keeps the page body readable and
 * lets a parent mount/unmount the subscription with the panel it belongs to.
 * Both require a `RealtimeProvider` above them (the hotel guard mounts it).
 */

import { useCallRoom, useHotelRoom } from '@hostesa/realtime-client';

export interface CallRoomSubscriptionProps {
  tenantId: string;
  hotelId: string;
  callId: string;
}

/** Subscribes to a single call room while mounted. Renders nothing. */
export function CallRoomSubscription({
  tenantId,
  hotelId,
  callId,
}: CallRoomSubscriptionProps): null {
  useCallRoom(tenantId, hotelId, callId);
  return null;
}

export interface HotelRoomSubscriptionProps {
  tenantId: string;
  hotelId: string;
}

/** Subscribes to the hotel room while mounted. Renders nothing. */
export function HotelRoomSubscription({ tenantId, hotelId }: HotelRoomSubscriptionProps): null {
  useHotelRoom(tenantId, hotelId);
  return null;
}
